import { useState, useEffect } from 'react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetFooter,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea, ScrollAreaViewport, ScrollAreaScrollbar } from '@/components/ui/scroll-area';
import { 
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { AlertCircle, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { CostCentre } from './cost-centres-table';

interface CreateCostCentrePanelProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (costCentre: Omit<CostCentre, 'id'>) => void;
  existingCodes: string[];
  parentNodeName?: string; // Optional: Show context when creating from structure view
}

interface FormData {
  code: string;
  name: string;
  type: string;
  billable: boolean;
  owner: string;
  description: string;
  status: 'Not Started' | 'In Progress' | 'On Hold' | 'Completed' | 'Cancelled';
  costsBudget: string;
  revenueBudget: string;
  hoursBudget: string;
  customFields: {
    [key: string]: string;
  };
}

interface FormErrors {
  code?: string;
  name?: string;
  type?: string;
  owner?: string;
  description?: string;
  costsBudget?: string;
  revenueBudget?: string;
  hoursBudget?: string;
}

const INITIAL_FORM_DATA: FormData = {
  code: '',
  name: '',
  type: '',
  billable: true,
  owner: '',
  description: '',
  status: 'Not Started',
  costsBudget: '',
  revenueBudget: '',
  hoursBudget: '',
  customFields: {},
};

const COST_CENTRE_TYPES = [
  'Department',
  'Project',
  'Product',
  'Service',
  'Location',
  'Division',
  'Team',
  'Other',
];

export function CreateCostCentrePanel({
  open,
  onOpenChange,
  onSave,
  existingCodes,
  parentNodeName,
}: CreateCostCentrePanelProps) {
  const [formData, setFormData] = useState<FormData>(INITIAL_FORM_DATA);
  const [errors, setErrors] = useState<FormErrors>({});
  const [touched, setTouched] = useState<Set<string>>(new Set());
  const [isSaving, setIsSaving] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [closeDialogOpen, setCloseDialogOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('basic');

  // Reset form when panel opens/closes
  useEffect(() => {
    if (open) {
      setFormData(INITIAL_FORM_DATA);
      setErrors({});
      setTouched(new Set());
      setHasUnsavedChanges(false);
      setActiveTab('basic');
    }
  }, [open]);

  // Track unsaved changes
  useEffect(() => {
    const hasChanges = 
      formData.code !== '' ||
      formData.name !== '' ||
      formData.type !== '' ||
      formData.owner !== '' ||
      formData.description !== '' ||
      formData.costsBudget !== '' ||
      formData.revenueBudget !== '' ||
      formData.hoursBudget !== '' ||
      Object.keys(formData.customFields).length > 0;
    
    setHasUnsavedChanges(hasChanges);
  }, [formData]);

  // Validation
  const validateField = (name: string, value: string | boolean): string | undefined => {
    switch (name) {
      case 'code':
        if (!value || (typeof value === 'string' && value.trim() === '')) {
          return 'Cost centre code is required';
        }
        if (typeof value === 'string' && existingCodes.includes(value.trim().toUpperCase())) {
          return 'This code already exists';
        }
        if (typeof value === 'string' && !/^[A-Z0-9&\-_]+$/i.test(value)) {
          return 'Code can only contain letters, numbers, hyphens, underscores, and ampersands';
        }
        if (typeof value === 'string' && value.length > 10) {
          return 'Maximum 10 characters allowed';
        }
        break;
      case 'name':
        if (!value || (typeof value === 'string' && value.trim() === '')) {
          return 'Cost centre name is required';
        }
        if (typeof value === 'string' && value.trim().length < 3) {
          return 'Name must be at least 3 characters';
        }
        if (typeof value === 'string' && value.length > 40) {
          return 'Maximum 40 characters allowed';
        }
        break;
      case 'type':
        if (!value || (typeof value === 'string' && value.trim() === '')) {
          return 'Type is required';
        }
        break;
      case 'description':
        if (typeof value === 'string' && value.length > 60) {
          return 'Maximum 60 characters allowed';
        }
        break;
      case 'costsBudget':
      case 'revenueBudget':
      case 'hoursBudget':
        if (value && typeof value === 'string' && value.trim() !== '') {
          const num = Number.parseFloat(value);
          if (Number.isNaN(num) || num < 0) {
            return 'Must be a positive number';
          }
        }
        break;
    }
    return undefined;
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};
    
    // Validate required fields
    const codeError = validateField('code', formData.code);
    if (codeError) newErrors.code = codeError;
    
    const nameError = validateField('name', formData.name);
    if (nameError) newErrors.name = nameError;
    
    const typeError = validateField('type', formData.type);
    if (typeError) newErrors.type = typeError;
    
    // Validate description length
    const descriptionError = validateField('description', formData.description);
    if (descriptionError) newErrors.description = descriptionError;
    
    // Validate budget fields if provided
    if (formData.costsBudget) {
      const costsBudgetError = validateField('costsBudget', formData.costsBudget);
      if (costsBudgetError) newErrors.costsBudget = costsBudgetError;
    }
    
    if (formData.revenueBudget) {
      const revenueBudgetError = validateField('revenueBudget', formData.revenueBudget);
      if (revenueBudgetError) newErrors.revenueBudget = revenueBudgetError;
    }
    
    if (formData.hoursBudget) {
      const hoursBudgetError = validateField('hoursBudget', formData.hoursBudget);
      if (hoursBudgetError) newErrors.hoursBudget = hoursBudgetError;
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleFieldChange = (name: string, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Validate on change if field has been touched
    if (touched.has(name)) {
      const error = validateField(name, value);
      setErrors(prev => {
        const newErrors = { ...prev };
        if (error) {
          newErrors[name as keyof FormErrors] = error;
        } else {
          delete newErrors[name as keyof FormErrors];
        }
        return newErrors;
      });
    }
  };

  const handleFieldBlur = (name: string) => {
    setTouched(prev => new Set(prev).add(name));
    const value = formData[name as keyof FormData];
    // Only validate if value is string or boolean (not customFields object)
    if (typeof value === 'string' || typeof value === 'boolean') {
      const error = validateField(name, value);
      if (error) {
        setErrors(prev => ({ ...prev, [name]: error }));
      }
    }
  };

  const handleSave = async () => {
    // Mark all fields as touched
    setTouched(new Set(['code', 'name', 'type', 'owner', 'costsBudget', 'revenueBudget', 'hoursBudget']));
    
    if (!validateForm()) {
      // Switch to basic tab if there are errors there
      if (errors.code || errors.name || errors.type || errors.owner) {
        setActiveTab('basic');
      }
      return;
    }

    setIsSaving(true);
    
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 500));
      
      const newCostCentre: Omit<CostCentre, 'id'> = {
        code: formData.code.trim().toUpperCase(),
        name: formData.name.trim(),
        type: formData.type,
        billable: formData.billable,
        owner: formData.owner.trim() || undefined,
        description: formData.description.trim() || undefined,
        status: formData.status,
        costsBudget: formData.costsBudget ? Number.parseFloat(formData.costsBudget) : 0,
        revenueBudget: formData.revenueBudget ? Number.parseFloat(formData.revenueBudget) : 0,
        hoursBudget: formData.hoursBudget ? Number.parseFloat(formData.hoursBudget) : 0,
        actualCosts: 0,
        committedCosts: 0,
        actualRevenue: 0,
        actualHours: 0,
        customFields: Object.keys(formData.customFields).length > 0 ? formData.customFields : undefined,
        createdAt: new Date(),
        updatedAt: new Date(),
        lastUpdated: new Date(),
      };
      
      onSave(newCostCentre);
      setHasUnsavedChanges(false);
      onOpenChange(false);
    } catch (error) {
      console.error('Error creating cost centre:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    if (hasUnsavedChanges) {
      setCloseDialogOpen(true);
    } else {
      onOpenChange(false);
    }
  };

  const handleDiscardChanges = () => {
    setCloseDialogOpen(false);
    setHasUnsavedChanges(false);
    onOpenChange(false);
  };

  return (
    <>
      <Sheet open={open} onOpenChange={handleClose}>
        <SheetContent className="sm:max-w-[600px] p-0 flex flex-col">
          <SheetHeader className="px-6 py-4 border-b">
            <SheetTitle>Create New Cost Centre</SheetTitle>
            <SheetDescription>
              {parentNodeName ? (
                <>
                  Adding to: <span className="font-medium text-foreground">{parentNodeName}</span>
                </>
              ) : (
                'Add a new cost centre to track expenses and revenue'
              )}
            </SheetDescription>
          </SheetHeader>

          <ScrollArea className="flex-1">
            <ScrollAreaViewport className="h-full">
              <div className="px-6 py-4">
                <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                  <TabsList className="grid w-full grid-cols-3 mb-6">
                    <TabsTrigger value="basic">Basic Info</TabsTrigger>
                    <TabsTrigger value="budgets">Budgets</TabsTrigger>
                    <TabsTrigger value="custom">Custom Fields</TabsTrigger>
                  </TabsList>

                  {/* Basic Info Tab */}
                  <TabsContent value="basic" className="space-y-4 mt-0">
                    {/* Code */}
                    <div className="space-y-2">
                      <Label htmlFor="code" className="required">
                        Cost Centre Code
                      </Label>
                      <Input
                        id="code"
                        value={formData.code}
                        onChange={(e) => handleFieldChange('code', e.target.value.toUpperCase())}
                        onBlur={() => handleFieldBlur('code')}
                        placeholder="e.g., CC-001, DEPT-IT"
                        maxLength={10}
                        className={cn(errors.code && touched.has('code') && 'border-destructive')}
                      />
                      {errors.code && touched.has('code') && (
                        <p className="text-sm text-destructive flex items-center gap-1">
                          <AlertCircle className="h-3 w-3" />
                          {errors.code}
                        </p>
                      )}
                      <p className="text-xs text-muted-foreground">
                        Unique identifier for this cost centre
                      </p>
                    </div>

                    {/* Name */}
                    <div className="space-y-2">
                      <Label htmlFor="name" className="required">
                        Cost Centre Name
                      </Label>
                      <Input
                        id="name"
                        value={formData.name}
                        onChange={(e) => handleFieldChange('name', e.target.value)}
                        onBlur={() => handleFieldBlur('name')}
                        placeholder="e.g., IT Department, Marketing Team"
                        maxLength={40}
                        className={cn(errors.name && touched.has('name') && 'border-destructive')}
                      />
                      {errors.name && touched.has('name') && (
                        <p className="text-sm text-destructive flex items-center gap-1">
                          <AlertCircle className="h-3 w-3" />
                          {errors.name}
                        </p>
                      )}
                    </div>

                    {/* Type */}
                    <div className="space-y-2">
                      <Label htmlFor="type" className="required">
                        Type
                      </Label>
                      <Select
                        value={formData.type}
                        onValueChange={(value) => handleFieldChange('type', value)}
                      >
                        <SelectTrigger 
                          id="type"
                          className={cn(errors.type && touched.has('type') && 'border-destructive')}
                          onBlur={() => handleFieldBlur('type')}
                        >
                          <SelectValue placeholder="Select type" />
                        </SelectTrigger>
                        <SelectContent>
                          {COST_CENTRE_TYPES.map((type) => (
                            <SelectItem key={type} value={type}>
                              {type}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {errors.type && touched.has('type') && (
                        <p className="text-sm text-destructive flex items-center gap-1">
                          <AlertCircle className="h-3 w-3" />
                          {errors.type}
                        </p>
                      )}
                    </div>

                    {/* Billable */}
                    <div className="flex items-center justify-between space-x-2 py-2">
                      <div className="space-y-0.5">
                        <Label htmlFor="billable">Billable</Label>
                        <p className="text-xs text-muted-foreground">
                          Can this cost centre be billed to clients?
                        </p>
                      </div>
                      <Switch
                        id="billable"
                        checked={formData.billable}
                        onCheckedChange={(checked) => handleFieldChange('billable', checked)}
                      />
                    </div>

                    {/* Owner */}
                    <div className="space-y-2">
                      <Label htmlFor="owner">Owner</Label>
                      <Input
                        id="owner"
                        value={formData.owner}
                        onChange={(e) => handleFieldChange('owner', e.target.value)}
                        onBlur={() => handleFieldBlur('owner')}
                        placeholder="e.g., John Smith"
                      />
                      <p className="text-xs text-muted-foreground">
                        Person responsible for this cost centre
                      </p>
                    </div>

                    {/* Status */}
                    <div className="space-y-2">
                      <Label htmlFor="status">Status</Label>
                      <Select
                        value={formData.status}
                        onValueChange={(value) => handleFieldChange('status', value as 'Not Started' | 'In Progress' | 'On Hold' | 'Completed' | 'Cancelled')}
                      >
                        <SelectTrigger id="status">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Not Started">Not Started</SelectItem>
                          <SelectItem value="In Progress">In Progress</SelectItem>
                          <SelectItem value="On Hold">On Hold</SelectItem>
                          <SelectItem value="Completed">Completed</SelectItem>
                          <SelectItem value="Cancelled">Cancelled</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Description */}
                    <div className="space-y-2">
                      <Label htmlFor="description">Description</Label>
                      <Textarea
                        id="description"
                        value={formData.description}
                        onChange={(e) => handleFieldChange('description', e.target.value)}
                        onBlur={() => handleFieldBlur('description')}
                        placeholder="Optional description of this cost centre"
                        rows={3}
                        maxLength={60}
                        className={cn(errors.description && touched.has('description') && 'border-destructive')}
                      />
                      <div className="flex justify-between items-center">
                        {errors.description && touched.has('description') ? (
                          <p className="text-sm text-destructive flex items-center gap-1">
                            <AlertCircle className="h-3 w-3" />
                            {errors.description}
                          </p>
                        ) : (
                          <span />
                        )}
                        <p className="text-xs text-muted-foreground">
                          {60 - formData.description.length} characters remaining
                        </p>
                      </div>
                    </div>
                  </TabsContent>

                  {/* Budgets Tab */}
                  <TabsContent value="budgets" className="space-y-4 mt-0">
                    <div className="rounded-lg border border-muted bg-muted/50 p-4 mb-4">
                      <p className="text-sm text-muted-foreground">
                        Set initial budget values for this cost centre. These can be updated later.
                      </p>
                    </div>

                    {/* Costs Budget */}
                    <div className="space-y-2">
                      <Label htmlFor="costsBudget">Costs Budget</Label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                          £
                        </span>
                        <Input
                          id="costsBudget"
                          type="number"
                          step="0.01"
                          min="0"
                          value={formData.costsBudget}
                          onChange={(e) => handleFieldChange('costsBudget', e.target.value)}
                          onBlur={() => handleFieldBlur('costsBudget')}
                          placeholder="0.00"
                          className={cn('pl-7', errors.costsBudget && touched.has('costsBudget') && 'border-destructive')}
                        />
                      </div>
                      {errors.costsBudget && touched.has('costsBudget') && (
                        <p className="text-sm text-destructive flex items-center gap-1">
                          <AlertCircle className="h-3 w-3" />
                          {errors.costsBudget}
                        </p>
                      )}
                    </div>

                    {/* Revenue Budget */}
                    <div className="space-y-2">
                      <Label htmlFor="revenueBudget">Revenue Budget</Label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                          £
                        </span>
                        <Input
                          id="revenueBudget"
                          type="number"
                          step="0.01"
                          min="0"
                          value={formData.revenueBudget}
                          onChange={(e) => handleFieldChange('revenueBudget', e.target.value)}
                          onBlur={() => handleFieldBlur('revenueBudget')}
                          placeholder="0.00"
                          className={cn('pl-7', errors.revenueBudget && touched.has('revenueBudget') && 'border-destructive')}
                        />
                      </div>
                      {errors.revenueBudget && touched.has('revenueBudget') && (
                        <p className="text-sm text-destructive flex items-center gap-1">
                          <AlertCircle className="h-3 w-3" />
                          {errors.revenueBudget}
                        </p>
                      )}
                    </div>

                    {/* Hours Budget */}
                    <div className="space-y-2">
                      <Label htmlFor="hoursBudget">Hours Budget</Label>
                      <Input
                        id="hoursBudget"
                        type="number"
                        step="0.5"
                        min="0"
                        value={formData.hoursBudget}
                        onChange={(e) => handleFieldChange('hoursBudget', e.target.value)}
                        onBlur={() => handleFieldBlur('hoursBudget')}
                        placeholder="0"
                        className={cn(errors.hoursBudget && touched.has('hoursBudget') && 'border-destructive')}
                      />
                      {errors.hoursBudget && touched.has('hoursBudget') && (
                        <p className="text-sm text-destructive flex items-center gap-1">
                          <AlertCircle className="h-3 w-3" />
                          {errors.hoursBudget}
                        </p>
                      )}
                    </div>
                  </TabsContent>

                  {/* Custom Fields Tab */}
                  <TabsContent value="custom" className="space-y-4 mt-0">
                    <div className="rounded-lg border border-muted bg-muted/50 p-4">
                      <p className="text-sm text-muted-foreground">
                        Custom fields are not yet implemented. This feature will allow you to add additional metadata to cost centres.
                      </p>
                    </div>
                  </TabsContent>
                </Tabs>
              </div>
            </ScrollAreaViewport>
            <ScrollAreaScrollbar orientation="vertical" />
          </ScrollArea>

          <SheetFooter className="px-6 py-4 border-t flex-row justify-between gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isSaving}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleSave}
              disabled={isSaving}
            >
              {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Create Cost Centre
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      {/* Unsaved Changes Dialog */}
      <AlertDialog open={closeDialogOpen} onOpenChange={setCloseDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Unsaved Changes</AlertDialogTitle>
            <AlertDialogDescription>
              You have unsaved changes. Are you sure you want to close without saving?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Continue Editing</AlertDialogCancel>
            <AlertDialogAction onClick={handleDiscardChanges} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Discard Changes
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
